import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { LogOut, Menu, Moon, Sun, Volume2, VolumeX, X } from "lucide-react";
import Crest from "@/components/Crest";
import { scrollToId } from "@/lib/scroll";
import { useAuth } from "@/context/AuthContext";
import { useEventState } from "@/lib/eventWindow";
import Magnetic from "@/components/Magnetic";
import { ambience } from "@/lib/ambience";

const LINKS = [
  { id: "#timeline", label: "Timeline" },
  { id: "#schedule", label: "Schedule" },
  { id: "#houses", label: "Houses" },
  { id: "#map", label: "Campus Map" },
  { id: "#quiz", label: "Doon Lore" },
  { id: "#gallery", label: "Gallery" },
  { id: "#guestbook", label: "Guestbook" },
];

export default function Navbar({ theme, onToggleTheme, onOpenAuth }) {
  const { user, logout } = useAuth();
  const ev = useEventState(60000);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [sound, setSound] = useState(false);
  const lastY = useRef(0);
  const { scrollY } = useScroll();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useMotionValueEvent(scrollY, "change", (y) => {
    if (y > 160 && y - lastY.current > 2) setHidden(true);
    else if (y < lastY.current - 2 || y <= 160) setHidden(false);
    lastY.current = y;
  });

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 60);
  };

  return (
    <>
      <motion.header
        animate={{ y: hidden && !open ? "-100%" : "0%" }}
        transition={{ type: "spring", stiffness: 200, damping: 24 }}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled ? "glass py-3" : "bg-transparent py-5"}`}
        data-testid="navbar"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6">
          <button onClick={() => go("#home")} className="flex items-center gap-3" data-testid="nav-brand">
            <Crest className="h-10 w-10" />
            <span className="text-left leading-tight">
              <span className="block font-display text-sm font-bold tracking-[0.2em] text-fg">THE DOON SCHOOL</span>
              <span className="block font-mono text-[10px] tracking-[0.3em] text-gold">FOUNDER'S DAY '26</span>
            </span>
          </button>

          <nav className="hidden items-center gap-7 lg:flex">
            {LINKS.map((l) => (
              <Magnetic key={l.id} strength={0.18}>
                <button
                  onClick={() => go(l.id)}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="relative text-[13px] font-medium tracking-wide text-soft transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-gold after:transition-transform after:duration-300 after:ease-out hover:text-gold hover:after:scale-x-100"
                >
                  {l.label}
                </button>
              </Magnetic>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {ev.phase === "live" && (
              <button
                onClick={() => go("#schedule")}
                data-testid="nav-live-pill"
                className="hidden items-center gap-2 whitespace-nowrap rounded-full bg-crimson px-4 py-2 font-display text-[10px] font-bold tracking-[0.2em] text-gold gold-on-crimson shadow-[0_4px_18px_rgba(139,0,0,0.4)] transition-transform duration-300 hover:scale-105 lg:flex"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ivory opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-ivory" />
                </span>
                LIVE · DAY {ev.day}
              </button>
            )}
            {user ? (
              <div className="glass hidden items-center gap-3 rounded-full py-1.5 pl-1.5 pr-3 sm:flex" data-testid="nav-user-chip">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold font-display text-xs font-bold text-navy">
                  {user.name?.[0]?.toUpperCase()}
                </span>
                <span className="text-xs font-semibold text-fg">{user.name.split(" ")[0]}</span>
                {user.role === "admin" && (
                  <span className="rounded-full bg-gold/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-gold">Admin</span>
                )}
                <button onClick={logout} aria-label="Sign out" data-testid="nav-logout-button" className="text-soft transition-colors hover:text-gold">
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                onClick={onOpenAuth}
                data-testid="nav-signin-button"
                className="glass hidden rounded-full px-6 py-2.5 font-display text-xs font-bold tracking-[0.2em] text-fg transition-all duration-300 hover:border-gold/40 hover:text-gold sm:block"
              >
                SIGN IN
              </button>
            )}
            <button
              onClick={() => {
                const nv = !sound;
                setSound(nv);
                if (nv) ambience.on();
                else ambience.off();
              }}
              aria-label={sound ? "Mute valley ambience" : "Play valley ambience"}
              data-testid="nav-ambience-toggle"
              className="glass flex h-10 w-10 items-center justify-center rounded-full text-fg transition-all duration-300 hover:text-gold hover:shadow-[0_0_20px_rgba(212,175,55,0.25)]"
            >
              {sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </button>
            <button
              onClick={onToggleTheme}
              data-testid="nav-theme-toggle"
              aria-label="Toggle theme"
              className="glass flex h-10 w-10 items-center justify-center rounded-full text-fg transition-all duration-300 hover:text-gold hover:shadow-[0_0_20px_rgba(212,175,55,0.25)]"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              onClick={() => go("#guestbook")}
              data-testid="nav-rsvp-button"
              className="ripple-host hidden rounded-full bg-gold px-6 py-2.5 font-display text-xs font-bold tracking-[0.2em] text-navy transition-transform duration-300 hover:scale-105 sm:block"
            >
              RSVP
            </button>
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid="nav-menu-toggle"
              aria-label="Menu"
              className="glass flex h-10 w-10 items-center justify-center rounded-full text-fg lg:hidden"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-6 bg-ink/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            {ev.phase === "live" && (
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="nav-mobile-live-pill"
                className="flex items-center gap-2 rounded-full bg-crimson px-5 py-2.5 font-display text-[10px] font-bold tracking-[0.2em] text-gold gold-on-crimson"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ivory opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-ivory" />
                </span>
                LIVE · DAY {ev.day} OF 3
              </motion.span>
            )}
            {LINKS.map((l, i) => (
              <motion.button
                key={l.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * i }}
                onClick={() => go(l.id)}
                className="font-display text-2xl font-bold tracking-widest text-fg transition-colors hover:text-gold"
              >
                {l.label}
              </motion.button>
            ))}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mt-4 flex flex-col items-center gap-4">
              {user ? (
                <button
                  onClick={() => { setOpen(false); logout(); }}
                  data-testid="nav-mobile-logout-button"
                  className="glass rounded-full px-8 py-3 font-display text-xs font-bold tracking-[0.2em] text-fg"
                >
                  SIGN OUT · {user.name.split(" ")[0].toUpperCase()}
                </button>
              ) : (
                <button
                  onClick={() => { setOpen(false); onOpenAuth(); }}
                  data-testid="nav-mobile-signin-button"
                  className="rounded-full bg-gold px-8 py-3 font-display text-xs font-bold tracking-[0.2em] text-navy"
                >
                  SIGN IN
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
